import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { default as Table } from 'antd/es/table';
import { useEffect, useState } from 'react';

import { hasValue } from 'utils/hasValue';

import { useOrganisationId } from 'hooks/context';

import { TransactionDetailModal } from './TransactionDetailModal';
import { transactionColumns } from './TransactionsTableColumns';
import {
  useGetCryptoTransactions,
  type CryptoTransaction
} from './useCryptoTransactions';

import { useVirtualBankAccount } from '../shared/VirtualBankAccountProvider';

type PaginationState = {
  page: number;
  cursorAfter?: string;
  cursorBefore?: string;
};

const INITIAL_PAGINATION: PaginationState = {
  page: 1,
  cursorAfter: undefined,
  cursorBefore: undefined
};

export const TransactionsTable = ({ isActive }: { isActive: boolean }) => {
  const organisationId = useOrganisationId<number>();
  const { virtualAccountNumber } = useVirtualBankAccount();

  const [selectedTransaction, setSelectedTransaction] =
    useState<CryptoTransaction | null>(null);
  const [pagination, setPagination] =
    useState<PaginationState>(INITIAL_PAGINATION);

  const { data: transactions, isFetching: isTransactionsFetching } =
    useGetCryptoTransactions(
      organisationId,
      {
        per_page: 8,
        cursor_after: pagination.cursorAfter,
        cursor_before: pagination.cursorBefore,
        virtual_account_number: virtualAccountNumber
      },
      {
        query: {
          enabled: isActive && hasValue(virtualAccountNumber),
          refetchInterval: pagination.page === 1 && isActive ? 10_000 : false,
          refetchIntervalInBackground: false
        }
      }
    );

  const nextCursor = transactions?.meta?.cursor_next;
  const previousCursor = transactions?.meta?.cursor_prev;
  const totalPages = transactions?.meta?.total
    ? Math.ceil(transactions.meta.total / (transactions.meta.per_page ?? 1))
    : undefined;

  useEffect(() => {
    setPagination(INITIAL_PAGINATION);
  }, [organisationId, virtualAccountNumber]);

  useEffect(() => {
    if (
      (pagination.cursorAfter || pagination.cursorBefore) &&
      !isTransactionsFetching &&
      transactions?.data?.length === 0
    ) {
      setPagination(INITIAL_PAGINATION);
    }
  }, [
    isTransactionsFetching,
    pagination.cursorAfter,
    pagination.cursorBefore,
    transactions?.data?.length
  ]);

  const handleNext = () => {
    if (nextCursor) {
      setPagination((prev) => ({
        page: prev.page + 1,
        cursorAfter: nextCursor,
        cursorBefore: undefined
      }));
    }
  };

  const handlePrevious = () => {
    if (previousCursor) {
      setPagination((prev) => {
        const newPage = Math.max(prev.page - 1, 1);
        if (newPage === 1) return INITIAL_PAGINATION;
        return {
          page: newPage,
          cursorAfter: undefined,
          cursorBefore: previousCursor
        };
      });
    }
  };

  return (
    <div>
      <Table<CryptoTransaction>
        dataSource={transactions?.data}
        columns={transactionColumns}
        className="h-[500px]"
        rowKey="record_uuid"
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={false}
        onRow={(record) => ({
          className: 'cursor-pointer',
          onClick: () => setSelectedTransaction(record)
        })}
      />
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-2">
          <LeftOutlined
            disabled={!transactions?.meta?.has_prev_page}
            onClick={handlePrevious}
          />
          <RightOutlined
            disabled={!transactions?.meta?.has_next_page}
            onClick={handleNext}
          />
          <span className="text-sm text-gray-500">
            {totalPages && `${pagination.page} of ${totalPages}`}
          </span>
        </div>
        {transactions?.meta?.total && (
          <span className="text-sm text-gray-500">
            total: {transactions.meta.total}
          </span>
        )}
      </div>
      {hasValue(selectedTransaction) && (
        <TransactionDetailModal
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      )}
    </div>
  );
};
